import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // If this gallery was inserted while still marked loading, clear once images ready.
    if (this.classList.contains('media-gallery--loading')) {
      this.#clearLoadingWhenReady();
    }
  }

  #controller = new AbortController();

  /** @type {number} */
  #loadToken = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Show skeleton as soon as the user picks a new variant (fetch can take 1–2s).
   */
  #handleVariantSelected = () => {
    this.#setLoading(true);
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const token = ++this.#loadToken;
    this.#setLoading(true);

    // Prefetch the primary images so replaceWith doesn't flash a blank gallery.
    await this.#preloadGalleryImages(newMediaGallery);

    if (token !== this.#loadToken || !this.isConnected) return;

    newMediaGallery.classList.remove('media-gallery--loading');
    newMediaGallery.removeAttribute('aria-busy');
    this.replaceWith(newMediaGallery);

    // Let PDP hover wiring rebind after the new gallery is in the DOM.
    // (Listening only to variant:update races this async preload/replace.)
    document.dispatchEvent(
      new CustomEvent('media-gallery:replaced', {
        bubbles: true,
        detail: { gallery: newMediaGallery },
      })
    );
  };

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.classList.toggle('media-gallery--loading', isLoading);
    if (isLoading) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Prefetch primary gallery images + hover metafield URLs into the browser cache.
   * @param {Element} gallery
   * @returns {Promise<void>}
   */
  #preloadGalleryImages(gallery) {
    const images = Array.from(gallery.querySelectorAll('img')).slice(0, 4);
    const hoverUrls = Array.from(gallery.querySelectorAll('[data-hover-image-url]'))
      .map((el) => el.getAttribute('data-hover-image-url'))
      .filter((url) => Boolean(url));

    /** @param {string} src @param {string} [srcset] @param {string} [sizes] */
    const preloadUrl = (src, srcset, sizes) =>
      new Promise((resolve) => {
        if (!src) {
          resolve();
          return;
        }

        const preloader = new Image();
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          resolve();
        };

        preloader.onload = done;
        preloader.onerror = done;
        if (sizes) preloader.sizes = sizes;
        if (srcset) preloader.srcset = srcset;
        preloader.src = src;
        setTimeout(done, 2000);
      });

    return Promise.all([
      ...images.map((img) => {
        if (!(img instanceof HTMLImageElement)) return Promise.resolve();
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return preloadUrl(img.getAttribute('src') || img.currentSrc, img.srcset, img.sizes);
      }),
      // Warm the hover image during the same wait so the first hover isn't cold.
      ...hoverUrls.map((url) => preloadUrl(/** @type {string} */ (url))),
    ]).then(() => undefined);
  }

  /**
   * Clear the loading skeleton once this gallery's primary image has painted.
   */
  #clearLoadingWhenReady() {
    const firstImage = this.querySelector('img');
    if (!(firstImage instanceof HTMLImageElement)) {
      this.#setLoading(false);
      return;
    }

    if (firstImage.complete && firstImage.naturalWidth > 0) {
      this.#setLoading(false);
      return;
    }

    const done = () => this.#setLoading(false);
    firstImage.addEventListener('load', done, { once: true });
    firstImage.addEventListener('error', done, { once: true });
    setTimeout(done, 2000);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
